import React, { useMemo, useState } from "react";
import { BadgeDelta, Card, type DeltaType, Title } from "@tremor/react";
import { Empty, Segmented, Skeleton, Switch } from "antd";
import { useTranslation } from "react-i18next";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { cn } from "@fitness-app/utils/src/styles/cn";

import {
  NEW_CLIENT_SEGMENTS,
  type NewClientsAggregate,
  type NewClientsDay,
  type NewClientSegment,
} from "~/modules/Dashboard/AppStats/newClients/aggregateNewClients";

interface NewClientsChartProps {
  current: NewClientsAggregate | null;
  /** The comparison window, so the delta compares like with like. */
  previous: NewClientsAggregate | null;
  /** Human label of the compared period, e.g. "1–4.08". */
  comparisonLabel: string;
  isLoading: boolean;
}

type ViewMode = "daily" | "cumulative";

interface Series {
  key: string;
  label: string;
  color: string;
  segment: NewClientSegment;
  total: number;
}

type ChartRow = Record<string, number | string> & { label: string; total: number };

// Fixed per tier so a tier keeps its colour whether or not the others are on screen. Gold is the
// brand's colour for 1:1 coaching. Validated for CVD separation on both card surfaces; gold sits
// at 2.4:1 against white, which is why the legend and tooltip always spell the values out.
// Trial wears rose: apart from the three paid hues in hue and lightness, also under CVD.
// Index 0 is the tier itself; 1–2 are the shades its top products wear when split; 3 is "other".
const SEGMENT_SHADES: Record<NewClientSegment, [string, string, string, string]> = {
  highTicket: ["#D4A017", "#E9C55C", "#A97F0E", "#E6D7A8"],
  lowTicket: ["#2F6FC4", "#7AA6E3", "#1E4F94", "#BCCDE8"],
  content: ["#3AA07A", "#86CBB0", "#24725A", "#BDDFCE"],
  trial: ["#C2417A", "#E08DB4", "#8E2A58", "#EEC5D8"],
};

/** Tiers that earn a legend entry only when they have clients in the period. */
const OPTIONAL_SEGMENTS: ReadonlySet<NewClientSegment> = new Set(["content", "trial"]);

const PRODUCTS_PER_SEGMENT = 3;
const SURFACE_GAP = 2;
const CORNER_RADIUS = 4;
const MAX_BAR_SIZE = 28;
/** Above this many columns a value on every cap turns into noise; the tooltip carries them. */
const MAX_LABELLED_COLUMNS = 16;

const formatNumber = (value: number) => Intl.NumberFormat("pl").format(value);

const axisTickClassName = cn("text-tremor-label", "fill-tremor-content", "dark:fill-dark-tremor-content");

interface StackedSegmentProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  fill?: string;
}

interface StackedSegmentShapeProps extends StackedSegmentProps {
  isTop: boolean;
}

/** A stacked-bar segment with a surface gap above it and, for the topmost one, rounded shoulders. */
const StackedSegment = ({ x = 0, y = 0, width = 0, height = 0, fill, isTop }: StackedSegmentShapeProps) => {
  const innerHeight = height - SURFACE_GAP;

  if (innerHeight <= 0 || width <= 0) {
    return null;
  }

  const top = y + SURFACE_GAP;
  const radius = isTop ? Math.min(CORNER_RADIUS, innerHeight, width / 2) : 0;
  const path = [
    `M${x},${top + radius}`,
    `a${radius},${radius} 0 0 1 ${radius},-${radius}`,
    `h${width - 2 * radius}`,
    `a${radius},${radius} 0 0 1 ${radius},${radius}`,
    `v${innerHeight - radius}`,
    `h-${width}`,
    "z",
  ].join(" ");

  return <path d={path} fill={fill} />;
};

interface NewClientsTooltipProps {
  active?: boolean;
  payload?: ReadonlyArray<{ payload?: unknown }>;
  label?: string | number;
  series: Series[];
}

const NewClientsTooltip = ({ active, payload, label, series }: NewClientsTooltipProps): React.ReactElement | null => {
  const { t } = useTranslation("dashboard");

  if (!active || !payload?.length) {
    return null;
  }

  const row = payload[0]?.payload as ChartRow | undefined;

  if (!row) {
    return null;
  }

  const rows = series.filter((item) => Number(row[item.key]) > 0);

  return (
    <div className="rounded-tremor-default border border-tremor-border bg-tremor-background text-tremor-default shadow-tremor-dropdown dark:border-dark-tremor-border dark:bg-dark-tremor-background">
      <p className="border-b border-tremor-border px-4 py-2 font-medium text-tremor-content-emphasis dark:border-dark-tremor-border dark:text-dark-tremor-content-emphasis">
        {label}
      </p>
      <div className="space-y-1 px-4 py-2">
        {rows.map((item) => (
          <div key={item.key} className="flex items-center justify-between gap-8">
            <div className="flex items-center gap-2">
              <span className="h-0.5 w-3 shrink-0 rounded-full" style={{ backgroundColor: item.color }} />
              <span className="whitespace-nowrap text-tremor-content dark:text-dark-tremor-content">{item.label}</span>
            </div>
            <span className="tabular-nums font-medium text-tremor-content-emphasis dark:text-dark-tremor-content-emphasis">
              {formatNumber(Number(row[item.key]))}
            </span>
          </div>
        ))}
        {rows.length > 1 ? (
          <div className="flex items-center justify-between gap-8 border-t border-tremor-border pt-1 dark:border-dark-tremor-border">
            <span className="text-tremor-content dark:text-dark-tremor-content">{t("newClients.total")}</span>
            <span className="tabular-nums font-medium text-tremor-content-emphasis dark:text-dark-tremor-content-emphasis">
              {formatNumber(row.total)}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const isTopSeries = (row: ChartRow, index: number, series: Series[]) =>
  series.slice(index + 1).every((other) => Number(row[other.key]) === 0);

const otherKey = (segment: NewClientSegment) => `other:${segment}`;

export const NewClientsChart = ({
  current,
  previous,
  comparisonLabel,
  isLoading,
}: NewClientsChartProps): React.ReactElement => {
  const { t } = useTranslation("dashboard");
  const [viewMode, setViewMode] = useState<ViewMode>("daily");
  const [splitByProduct, setSplitByProduct] = useState(false);

  const legendSegments = useMemo(
    () =>
      NEW_CLIENT_SEGMENTS.filter((segment) => !OPTIONAL_SEGMENTS.has(segment) || (current?.totals[segment] ?? 0) > 0),
    [current?.totals],
  );

  // An all-zero series still draws its stroke, exactly on top of the one below it, so only series
  // with clients are plotted. The legend keeps the zero so the reader knows the tier exists.
  const series = useMemo<Series[]>(() => {
    if (!current) {
      return [];
    }

    return legendSegments.flatMap((segment): Series[] => {
      const shades = SEGMENT_SHADES[segment];
      const segmentLabel = t(`newClients.segments.${segment}`);

      if (!splitByProduct) {
        return current.totals[segment] > 0
          ? [{ key: segment, label: segmentLabel, color: shades[0], segment, total: current.totals[segment] }]
          : [];
      }

      const products = current.products.filter((product) => product.segment === segment);
      const top = products.slice(0, PRODUCTS_PER_SEGMENT);
      const rest = products.slice(PRODUCTS_PER_SEGMENT);
      const restTotal = rest.reduce((acc, product) => acc + product.total, 0);

      return [
        ...top.map((product, index) => ({
          key: product.id,
          label: product.name,
          color: shades[index] ?? shades[0],
          segment,
          total: product.total,
        })),
        ...(restTotal > 0
          ? [
              {
                key: otherKey(segment),
                label: `${t("newClients.other")} · ${segmentLabel}`,
                color: shades[3],
                segment,
                total: restTotal,
              },
            ]
          : []),
      ];
    });
  }, [current, legendSegments, splitByProduct, t]);

  const { dailyRows, cumulativeRows } = useMemo(() => {
    const valueOf = (day: NewClientsDay, item: Series): number => {
      if (item.key === item.segment) {
        return day[item.segment];
      }
      if (item.key === otherKey(item.segment)) {
        const named = series
          .filter((other) => other.segment === item.segment && other.key !== item.key)
          .reduce((acc, other) => acc + (day.byProduct[other.key] ?? 0), 0);
        return day[item.segment] - named;
      }
      return day.byProduct[item.key] ?? 0;
    };

    const dailyRows: ChartRow[] = (current?.daily ?? []).map((day) => ({
      label: day.label,
      total: day.total,
      ...Object.fromEntries(series.map((item) => [item.key, valueOf(day, item)])),
    }));

    const running: Record<string, number> = {};
    let runningTotal = 0;
    const cumulativeRows: ChartRow[] = dailyRows.map((row) => {
      runningTotal += row.total;
      const out: ChartRow = { label: row.label, total: runningTotal };
      for (const item of series) {
        const next = (running[item.key] ?? 0) + Number(row[item.key]);
        running[item.key] = next;
        out[item.key] = next;
      }
      return out;
    });

    return { dailyRows, cumulativeRows };
  }, [current?.daily, series]);

  const rows = viewMode === "daily" ? dailyRows : cumulativeRows;
  const total = current?.total ?? 0;
  const previousTotal = previous?.total ?? null;
  const avgPerDay = current?.daily.length ? Math.round(total / current.daily.length) : 0;
  const delta = previousTotal ? Math.round(((total - previousTotal) / previousTotal) * 100) : null;
  const deltaType: DeltaType = delta === null || delta === 0 ? "unchanged" : delta > 0 ? "increase" : "decrease";
  const showCapLabels = viewMode === "daily" && rows.length <= MAX_LABELLED_COLUMNS;

  const renderTooltip = (props: Omit<NewClientsTooltipProps, "series">) => (
    <NewClientsTooltip {...props} series={series} />
  );

  const axes = (
    <>
      <CartesianGrid
        vertical={false}
        className={cn("stroke-1", "stroke-tremor-border", "dark:stroke-dark-tremor-border")}
      />
      <XAxis
        dataKey="label"
        tickLine={false}
        axisLine={false}
        minTickGap={24}
        tickMargin={8}
        fill=""
        stroke=""
        className={axisTickClassName}
      />
      <YAxis
        width={36}
        allowDecimals={false}
        tickLine={false}
        axisLine={false}
        tickFormatter={formatNumber}
        fill=""
        stroke=""
        className={axisTickClassName}
      />
    </>
  );

  return (
    <Card className="h-full">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
        <div>
          <Title>{t("newClients.title")}</Title>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-3xl font-semibold leading-none text-tremor-content-emphasis dark:text-dark-tremor-content-emphasis">
              {formatNumber(total)}
            </span>
            {delta !== null ? (
              <BadgeDelta deltaType={deltaType} size="xs">
                {delta > 0 ? "+" : ""}
                {delta}%
              </BadgeDelta>
            ) : null}
            <span className="text-tremor-default text-tremor-content dark:text-dark-tremor-content">
              {previousTotal !== null ? (
                <>
                  {comparisonLabel}: <span className="tabular-nums">{formatNumber(previousTotal)}</span> ·{" "}
                </>
              ) : null}
              {t("newClients.avgPerDay", { count: avgPerDay })}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <label className="flex cursor-pointer items-center gap-2 text-tremor-default text-tremor-content dark:text-dark-tremor-content">
            <Switch size="small" checked={splitByProduct} onChange={setSplitByProduct} />
            {t("newClients.splitByProduct")}
          </label>
          <Segmented
            value={viewMode}
            onChange={(value) => setViewMode(value as ViewMode)}
            options={[
              { label: t("newClients.daily"), value: "daily" },
              { label: t("newClients.cumulative"), value: "cumulative" },
            ]}
          />
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2">
        {legendSegments.map((segment) => {
          const segmentSeries = series.filter((item) => item.segment === segment);
          return (
            <div key={segment} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-tremor-default">
              <span className="flex items-center gap-2">
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: SEGMENT_SHADES[segment][0] }}
                />
                <span className="text-tremor-content dark:text-dark-tremor-content">
                  {t(`newClients.segments.${segment}`)}
                </span>
                <span className="tabular-nums font-semibold text-tremor-content-emphasis dark:text-dark-tremor-content-emphasis">
                  {formatNumber(current?.totals[segment] ?? 0)}
                </span>
              </span>
              {splitByProduct
                ? segmentSeries.map((item) => (
                    <span
                      key={item.key}
                      className="flex items-center gap-1.5 rounded-tremor-full bg-tremor-background-subtle px-2 py-0.5 text-tremor-label text-tremor-content dark:bg-dark-tremor-background-subtle dark:text-dark-tremor-content"
                    >
                      <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: item.color }} />
                      {item.label}
                      <span className="tabular-nums font-medium text-tremor-content-emphasis dark:text-dark-tremor-content-emphasis">
                        {formatNumber(item.total)}
                      </span>
                    </span>
                  ))
                : null}
            </div>
          );
        })}
      </div>

      <div className="mt-6 h-80">
        {isLoading && !current ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : total === 0 ? (
          <Empty className="pt-12" image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("newClients.noData")} />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            {viewMode === "daily" ? (
              <BarChart data={rows} margin={{ top: 16, right: 12, left: 8, bottom: 0 }} maxBarSize={MAX_BAR_SIZE}>
                {axes}
                <Tooltip content={renderTooltip} cursor={{ fill: "#6b7280", fillOpacity: 0.08 }} />
                {series.map((item, index) => (
                  <Bar
                    key={item.key}
                    dataKey={item.key}
                    stackId="clients"
                    fill={item.color}
                    isAnimationActive={false}
                    shape={(props: unknown) => {
                      // recharts types the shape callback loosely; only the geometry and the row are needed.
                      const { payload, ...geometry } = props as StackedSegmentProps & { payload?: ChartRow };
                      return (
                        <StackedSegment {...geometry} isTop={payload ? isTopSeries(payload, index, series) : false} />
                      );
                    }}
                  >
                    {showCapLabels && index === series.length - 1 ? (
                      <LabelList
                        dataKey="total"
                        position="top"
                        offset={6}
                        className={axisTickClassName}
                        formatter={(value: number) => (value > 0 ? formatNumber(value) : "")}
                      />
                    ) : null}
                  </Bar>
                ))}
              </BarChart>
            ) : (
              <AreaChart data={rows} margin={{ top: 16, right: 12, left: 8, bottom: 0 }}>
                <defs>
                  {series.map((item) => (
                    <linearGradient key={item.key} id={`new-clients-${item.key}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={item.color} stopOpacity={0.28} />
                      <stop offset="100%" stopColor={item.color} stopOpacity={0.04} />
                    </linearGradient>
                  ))}
                </defs>
                {axes}
                <Tooltip content={renderTooltip} cursor={{ stroke: "#6b7280", strokeOpacity: 0.4, strokeWidth: 1 }} />
                {series.map((item) => (
                  <Area
                    key={item.key}
                    type="monotone"
                    dataKey={item.key}
                    stackId="clients"
                    stroke={item.color}
                    strokeWidth={2}
                    fill={`url(#new-clients-${item.key})`}
                    isAnimationActive={false}
                    activeDot={{ r: 4, strokeWidth: 2, stroke: "#ffffff", fill: item.color }}
                  />
                ))}
              </AreaChart>
            )}
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
};
